import React, { useEffect, useState } from 'react'; // Добавляем useState
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import { DataProvider, useData } from './context/DataContext';
import BottomNavigation from './components/layout/BottomNavigation';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
// import MailingDetailPage from './pages/MailingDetailPage'; // Удаляем импорт
import BlockedPage from './pages/BlockedPage';
import TelegramAuthRequiredPage from './pages/TelegramAuthRequiredPage';
import LoadingScreen from './components/LoadingScreen'; // Импортируем LoadingScreen
import HapticFeedbackProvider from './components/HapticFeedbackProvider'; // Импортируем HapticFeedbackProvider
import { APP_VERSION, forceAppUpdate } from './version'; // Добавляем импорт forceAppUpdate
import './App.css';

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <DataProvider> {/* Оборачиваем Router в DataProvider */}
          <Router>
            <HapticFeedbackProvider>
              <AppContent />
            </HapticFeedbackProvider>
          </Router>
        </DataProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

// Компонент для основного содержимого приложения после инициализации контекстов
function AppContent() {
  const { isBlocked, isTelegramAuthRequiredAndMissing, loading: authLoading } = useAuth();
  useSettings();
  const location = useLocation();
  // Убираем areStoriesLoading, isContentLoading, areTagsLoading, так как они больше не используются напрямую здесь
  const { isInitialHomeLoadComplete } = useData(); 
  const [telegramWebAppStyle, setTelegramWebAppStyle] = useState(null);
  const [minimumLoadingTimeElapsed, setMinimumLoadingTimeElapsed] = useState(false);

  // Таймер для минимального времени показа загрузочного экрана (2 секунды)
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimumLoadingTimeElapsed(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready(); // Сообщаем Telegram, что приложение готово
      // Принудительно устанавливаем черный фон
      const bgColor = '#000000'; 
      setTelegramWebAppStyle({ backgroundColor: bgColor });
      
      // Отключаем свайп для закрытия приложения
      tg.disableVerticalSwipes();
      
      // Если нужно реагировать на изменение темы в реальном времени (опционально)
      // и если вы все же захотите использовать цвет темы, раскомментируйте:
      // tg.onEvent('themeChanged', () => {
      //   const updatedBgColor = tg.backgroundColor || '#000000'; // Снова проверяем на случай undefined
      //   setTelegramWebAppStyle({ backgroundColor: updatedBgColor });
      // });
    } else {
      // Если Telegram WebApp API недоступно, также используем черный фон
      setTelegramWebAppStyle({ backgroundColor: '#000000' });
    }
    
    // Сохраняем текущую версию приложения
    const savedVersion = localStorage.getItem('app_version');
    if (savedVersion && savedVersion !== APP_VERSION) {
      // Версия изменилась - очищаем кеш
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }
    }
    localStorage.setItem('app_version', APP_VERSION);
    
    // Проверка версии каждые 5 минут
    const versionCheckInterval = setInterval(() => {
      fetch('/api/version', {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      })
      .then(res => res.json())
      .then(data => {
        if (data.version && data.version !== APP_VERSION) {
          // Новая версия доступна - перезагружаем приложение с использованием forceAppUpdate
          console.log(`Обнаружена новая версия: ${data.version}, текущая: ${APP_VERSION}. Принудительное обновление...`);
          forceAppUpdate();
        }
      })
      .catch(() => {
        // Игнорируем ошибки проверки версии
      });
    }, 5 * 60 * 1000); // 5 минут
    
    return () => clearInterval(versionCheckInterval);
  }, []);

  // Определяем, все ли готово для отображения основного контента
  const isTelegramApiReady = telegramWebAppStyle !== null; // Telegram API обработан

  // Глобальный LoadingScreen показывается если:
  // 1. Telegram API еще не готов (isTelegramApiReady = false)
  // 2. ИЛИ идет загрузка аутентификации (authLoading = true)
  // 3. ИЛИ мы на главной странице (location.pathname === '/') И первоначальная загрузка всех данных для главной еще не завершена (!isInitialHomeLoadComplete)
  // 4. ИЛИ минимальное время показа (2 секунды) еще не прошло
  const showGlobalLoadingScreen = !isTelegramApiReady || authLoading || (location.pathname === '/' && !isInitialHomeLoadComplete) || !minimumLoadingTimeElapsed;

  if (showGlobalLoadingScreen) {
    // Показываем LoadingScreen, пока Telegram API не готов или идет ПЕРВОНАЧАЛЬНАЯ загрузка данных для главной
    // или идет процесс аутентификации.
    // Передаем цвет фона, если он уже получен от Telegram API
    return <LoadingScreen backgroundColor={telegramWebAppStyle?.backgroundColor} />;
  }
  
  if (isTelegramAuthRequiredAndMissing) {
    return <TelegramAuthRequiredPage />;
  }
  
  if (isBlocked) {
    return <BlockedPage />;
  }

  return (
    <div className="App"> {/* Возвращаем стиль и убираем app-container */}
      <Routes location={location}>
        <Route path="/" element={<HomePage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/admin" element={<AdminPage />} />
        {/* <Route path="/admin/mailings/:mailingId" element={<MailingDetailPage />} /> */} {/* Удаляем маршрут */}
        <Route path="/content/:id" element={<div className="placeholder">Страница контента в разработке</div>} />
        <Route path="/blocked" element={<BlockedPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      <BottomNavigation />
    </div>
  );
}

export default App;
